import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { api, formatApiErrorDetail } from '@/lib/api';
import { Upload, FileText, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

/**
 * CSV-import dialog for the Customers page.
 *
 * Flow:
 *   1. user clicks "Tuo CSV" button to open the dialog
 *   2. picks a CSV file → POST dry_run=true → backend returns preview {updated, created, skipped, errors, preview[]}
 *   3. preview shown in a scrollable list
 *   4. user clicks "Vahvista tuonti" → same file POSTed without dry_run → success/error toast → onImported() refresh
 */
export default function CsvImportDialog({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setLoading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose && onClose();
  };

  const handleFile = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      toast.error('Valitse .csv-tiedosto');
      return;
    }
    setFile(f);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await api.post('/customers/import.csv?dry_run=true', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(data);
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || e.message;
      toast.error(`Esikatselu epäonnistui: ${msg}`);
      reset();
    } finally {
      setLoading(false);
    }
  };

  const confirmApply = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/customers/import.csv', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Tuonti valmis — ${data.created} luotu, ${data.updated} päivitetty, ${data.skipped} ennallaan${data.errors?.length ? `, ${data.errors.length} virhettä` : ''}`);
      onImported && onImported();
      handleClose();
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || e.message;
      toast.error(`Tuonti epäonnistui: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const totalRows = preview ? (preview.created + preview.updated + preview.skipped + (preview.errors?.length || 0)) : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="customers-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Tuo asiakkaat CSV-tiedostosta</DialogTitle>
          <DialogDescription>
            Käytä samaa muotoa kuin &quot;Vie CSV&quot; tuottaa (;-erotin, UTF-8). Olemassa olevat asiakkaat tunnistetaan <code>nro</code>-sarakkeen perusteella; ilman <code>nro</code>:a löydetään <code>ketju + asiakas</code>-yhdistelmällä. Ei-löytyvät rivit luodaan uusiksi asiakkaiksi.
          </DialogDescription>
        </DialogHeader>

        {!file && (
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) handleFile(f);
            }}
            className="border-2 border-dashed border-[#E5E6E3] rounded-xl py-12 px-6 text-center cursor-pointer hover:border-[#2A4B30] hover:bg-[#F9FAF8] transition-colors"
            data-testid="customers-import-dropzone"
          >
            <Upload className="mx-auto mb-3 text-[#6A6D66]" size={32} />
            <p className="font-medium text-[#1C1C1A]">Klikkaa tai pudota CSV-tiedosto tähän</p>
            <p className="text-xs text-[#6A6D66] mt-1">Vain .csv — UTF-8 + ;-erotin (kuten &quot;Vie CSV&quot; -ulostulo)</p>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              data-testid="customers-import-file-input"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>
        )}

        {file && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm bg-[#F9FAF8] border border-[#E5E6E3] rounded-lg px-3 py-2">
              <FileText size={16} className="text-[#2A4B30]" />
              <span className="flex-1 truncate font-medium">{file.name}</span>
              <button
                type="button"
                onClick={reset}
                data-testid="customers-import-reset"
                className="text-[11px] text-[#6A6D66] hover:text-[#1C1C1A] underline"
              >
                Vaihda tiedosto
              </button>
            </div>

            {loading && (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-[#6A6D66]">
                <RefreshCw size={14} className="animate-spin" />
                Lasketaan esikatselua…
              </div>
            )}

            {preview && !loading && (
              <>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <Stat label="Yhteensä" value={totalRows} />
                  <Stat label="Luodaan" value={preview.created} color="green" testId="csv-import-stat-created" />
                  <Stat label="Päivitetään" value={preview.updated} color="blue" testId="csv-import-stat-updated" />
                  <Stat label="Ennallaan" value={preview.skipped} color="muted" testId="csv-import-stat-skipped" />
                </div>

                {preview.errors?.length > 0 && (
                  <div className="bg-[#FBEAE5] border border-[#F5C9BD] rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1.5 text-[#C05A3C] font-medium text-sm">
                      <AlertTriangle size={14} />
                      {preview.errors.length} virhettä — ei voida jatkaa
                    </div>
                    <ul className="text-xs space-y-1 max-h-28 overflow-y-auto">
                      {preview.errors.slice(0, 20).map((er, i) => (
                        <li key={i} data-testid={`csv-import-error-${i}`}>
                          Rivi {er.row}: {er.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {preview.preview?.length > 0 && (
                  <div className="border border-[#E5E6E3] rounded-lg max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#F4F6F3] text-[#6A6D66]">
                        <tr>
                          <th className="px-2 py-1.5 text-left">Rivi</th>
                          <th className="px-2 py-1.5 text-left">Toiminto</th>
                          <th className="px-2 py-1.5 text-left">Nro</th>
                          <th className="px-2 py-1.5 text-left">Asiakas</th>
                          <th className="px-2 py-1.5 text-left">Muutokset</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.preview.slice(0, 200).map((p, i) => (
                          <tr key={i} className="border-t border-[#F0F1ED]" data-testid={`csv-import-preview-row-${i}`}>
                            <td className="px-2 py-1 text-[#6A6D66]">{p.row}</td>
                            <td className={`px-2 py-1 font-medium ${
                              p.action === 'create' ? 'text-[#2A6E36]' :
                              p.action === 'update' ? 'text-[#3866B7]' : 'text-[#6A6D66]'
                            }`}>
                              {p.action === 'create' ? 'Luo' : p.action === 'update' ? 'Päivitä' : 'Ennallaan'}
                            </td>
                            <td className="px-2 py-1 font-mono text-[#6A6D66]">{p.nro || '—'}</td>
                            <td className="px-2 py-1">{p.asiakas}</td>
                            <td className="px-2 py-1 text-[#6A6D66]">
                              {p.changes?.length ? p.changes.join(', ') : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {preview.preview.length > 200 && (
                      <div className="text-center text-[11px] text-[#6A6D66] py-1.5 border-t border-[#F0F1ED]">
                        …ja vielä {preview.preview.length - 200} riviä
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} data-testid="customers-import-cancel">
            Peruuta
          </Button>
          <Button
            onClick={confirmApply}
            disabled={!preview || loading || (preview?.errors?.length || 0) > 0 || (preview && (preview.created + preview.updated) === 0)}
            className="bg-[#2A4B30] hover:bg-[#1E3622] text-white"
            data-testid="customers-import-confirm"
          >
            <CheckCircle2 size={16} className="mr-1.5" />
            Vahvista tuonti
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, color = 'default', testId }) {
  const colorClasses = {
    green: 'bg-[#E8F0E9] text-[#1E3622]',
    blue: 'bg-[#E5EBF8] text-[#1E3A7C]',
    muted: 'bg-[#F4F6F3] text-[#6A6D66]',
    default: 'bg-[#F4F6F3] text-[#1C1C1A]',
  };
  return (
    <div className={`rounded-lg px-3 py-2 ${colorClasses[color] || colorClasses.default}`} data-testid={testId}>
      <div className="text-2xl font-bold leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}
